import type {ReactElement} from 'react';
import {useState} from 'react';
import styles from './share-email-in-team-toggler.module.scss';
import {ShareInTeamBarState} from '@Components/share-in-team-toggle-bar/share-in-team-toggle-bar.types';
import {ShareInTeamToggleBar} from '../share-in-team-toggle-bar/share-in-team-toggle-bar';
import type {EmailCampaign} from '@Components/email-marketing-campaigns/email-marketing-campaigns.types';
import {shareEmailMarketingCampaignInTeam} from '@Libraries/email-campaigns-library';
import {isUserPremium} from '@Libraries/user.library';
import {noop} from '@Utils/general.util';
import {CollaborationGA4Source, CollaborationGA4Toggle, GA4EventParamName} from '@Libraries/ga-events';

interface ShareEmailInTeamTogglerProps {
  campaign: EmailCampaign;
  onStateUpdate?: (state: ShareInTeamBarState) => void;
  gaEvent?: string;
  gaSource?: string;
}

export function ShareEmailInTeamToggler({campaign, onStateUpdate = noop, gaEvent = '', gaSource = ''}: ShareEmailInTeamTogglerProps): ReactElement<any> {

  const getToggleStateByCampaignState = (): ShareInTeamBarState => {
    if (!isUserPremium()) {
      return ShareInTeamBarState.UPSELLING;
    }

    if (campaign.isSharedInTeam) {
      return ShareInTeamBarState.ACTIVE;
    }

    return ShareInTeamBarState.INACTIVE;
  };

  const [isLoading, setIsLoading] = useState(false);
  const [toggleState, setToggleState] = useState(getToggleStateByCampaignState());

  const updateSharingStatus = async (shareInTeamState: ShareInTeamBarState): Promise<void> => {
    let isSharedInTeam = false;
    if (shareInTeamState === ShareInTeamBarState.ACTIVE) {
      isSharedInTeam = true;
    }

    setIsLoading(true);
    await shareEmailMarketingCampaignInTeam(isSharedInTeam, campaign.id);
    setIsLoading(false);
    setToggleState(shareInTeamState);
    onStateUpdate(shareInTeamState);

    if (gaEvent) {
      window.PMW.gtm.trackGA4CustomEvent(gaEvent, {
        [GA4EventParamName.TYPE]: isSharedInTeam ? CollaborationGA4Toggle.SHARED : CollaborationGA4Toggle.UNSHARED,
        [GA4EventParamName.SOURCE]: gaSource ?? CollaborationGA4Source.EMAIL_EDITOR
      });
    }
  };

  return (
    <div className={styles.container}>
      <ShareInTeamToggleBar
        state={toggleState}
        isCurrentlyLoading={isLoading}
        onStateUpdate={(shareInTeamState: ShareInTeamBarState): void => {
          void updateSharingStatus(shareInTeamState);
        }}
      />
    </div>
  );
}
